import React, { useState } from 'react';
import { format } from 'date-fns';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

const formatNumber = (num) => {
  return num.toLocaleString();
};

const ReviewsTable = ({ reviews }) => {
  const [viewMode, setViewMode] = useState('table');
  const [currentPage, setCurrentPage] = useState(1);
  const [reviewsPerPage] = useState(50);

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const renderStars = (rating) => {
    return '★'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  const getSegmentColor = (segment) => {
    const colors = {
      'General Hotel': '#2563eb',
      'Ski Mountain': '#dc2626', 
      'Aquatopia': '#0891b2',
      'Camelbeach': '#ea580c',
      'Mountain Adventures': '#16a34a'
    };
    return colors[segment] || '#6b7280';
  };

  // Pagination calculations
  const totalPages = Math.ceil(reviews.length / reviewsPerPage);
  const startIndex = (currentPage - 1) * reviewsPerPage;
  const endIndex = startIndex + reviewsPerPage;
  const currentReviews = reviews.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const goToPrevPage = () => setCurrentPage(Math.max(1, currentPage - 1));
  const goToNextPage = () => setCurrentPage(Math.min(totalPages, currentPage + 1));

  if (reviews.length === 0) {
    return (
      <div className="reviews-container">
        <div className="reviews-header">
          <h2>Reviews ({formatNumber(reviews.length)})</h2>
        </div>
        <div className="no-reviews">No reviews found matching your filters.</div>
      </div>
    );
  }

  return (
    <div className="reviews-container">
      <div className="reviews-header">
        <h2>Reviews ({formatNumber(reviews.length)})</h2>
        <div className="header-controls">
          <div className="pagination-info">
            Showing {formatNumber(startIndex + 1)}-{formatNumber(Math.min(endIndex, reviews.length))} of {formatNumber(reviews.length)}
          </div>
          <div className="view-toggle">
            <button 
              className={viewMode === 'table' ? 'active' : ''}
              onClick={() => setViewMode('table')}
            >
              Table
            </button>
            <button 
              className={viewMode === 'cards' ? 'active' : ''}
              onClick={() => setViewMode('cards')}
            >
              Cards
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'table' ? (
        <div className="table-container">
          <table className="reviews-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Rating</th>
                <th>Segment</th>
                <th>Review</th>
              </tr>
            </thead>
            <tbody>
              {currentReviews.map((review, index) => (
                <tr key={index}>
                  <td className="date-cell">{formatDate(review.DATE)}</td>
                  <td className="rating-cell">
                    <span className="stars">{renderStars(review.RATING)}</span>
                    <span className="rating-number">({review.RATING})</span>
                  </td>
                  <td className="segment-cell">
                    <span 
                      className="segment-badge"
                      style={{ backgroundColor: getSegmentColor(review.SEGMENT) }}
                    >
                      {review.SEGMENT}
                    </span>
                  </td>
                  <td className="review-cell">{review.REVIEW}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="cards-container">
          {currentReviews.map((review, index) => (
            <div key={index} className="review-card">
              <div className="card-header">
                <div className="card-date">{formatDate(review.DATE)}</div>
                <div className="card-rating">
                  <span className="stars">{renderStars(review.RATING)}</span>
                  <span className="rating-number">({review.RATING})</span>
                </div>
              </div>
              <div className="card-segment">
                <span 
                  className="segment-badge"
                  style={{ backgroundColor: getSegmentColor(review.SEGMENT) }}
                >
                  {review.SEGMENT}
                </span>
              </div>
              <div className="card-review">{review.REVIEW}</div>
            </div>
          ))}
        </div>
      )}
      
      {totalPages > 1 && (
        <div className="pagination-container">
          <div className="pagination-info-bottom">
            Page {currentPage} of {formatNumber(totalPages)} ({formatNumber(reviews.length)} total reviews)
          </div>
          <div className="pagination-controls">
            <button 
              className="pagination-btn" 
              onClick={goToFirstPage} 
              disabled={currentPage === 1}
              title="First page"
            >
              <ChevronsLeft size={16} />
            </button>
            <button 
              className="pagination-btn" 
              onClick={goToPrevPage} 
              disabled={currentPage === 1}
              title="Previous page"
            >
              <ChevronLeft size={16} />
            </button>
            
            <div className="page-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => goToPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button 
              className="pagination-btn" 
              onClick={goToNextPage} 
              disabled={currentPage === totalPages}
              title="Next page"
            >
              <ChevronRight size={16} />
            </button>
            <button 
              className="pagination-btn" 
              onClick={goToLastPage} 
              disabled={currentPage === totalPages}
              title="Last page"
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsTable;